const isMissing = (value) => value === undefined || value === "";

export const isMultiRowPg = (item, isAlphaNumericFn) => {
  const cf = (item[15] || "").trim();
  const pIva = (item[9] || "").trim();

  return (
    (pIva !== "" && cf !== pIva && cf.length === 16 && isAlphaNumericFn(cf)) ||
    /^\d{11}$/.test(cf) ||
    (pIva !== "" && cf === pIva) ||
    cf === ""
  );
};

const buildReasons = (checks) => {
  let reasons = "";

  checks.forEach((check) => {
    if (check.when) {
      reasons += check.message;
    }
  });

  return reasons;
};

export const getUploadRowContext = ({
  item,
  index,
  importType,
  isAlphaNumericFn,
}) => {
  const cf = (item[15] || "").trimStart().trimEnd();
  const pIva = (item[9] || "").trimStart().trimEnd();

  let isPg = false;
  if (importType === "aricdesktop") {
    const rowType = cf !== "" && pIva !== "" ? "N" : item[3];
    isPg = rowType === "S" ? false : true;
  } else {
    isPg = isMultiRowPg(item, isAlphaNumericFn);
  }

  return {
    id: importType === "multi" ? (item[0] !== "" ? item[0] : index) : item[0],
    cf,
    pIva,
    isPg,
    denominazione: item[1],
    professione: item[2],
    addrComune: item[11],
    addrProvincia: item[12],
    addrCap: item[14],
    nascitaData: item[16],
    nascitaComune: item[17],
    identDate: item[22],
  };
};

export const shouldIncludeUploadRow = (rows, { isPg, cf, pIva }) => {
  if (!isPg) {
    return rows.filter((x) => x.isPg === false && x.cf === cf).length <= 0;
  }

  return rows.filter((x) => x.isPg === true && x.pIva === pIva).length <= 0;
};

export const evaluateAricCompleteness = (context) => {
  const {
    isPg,
    cf,
    pIva,
    denominazione,
    professione,
    nascitaComune,
    nascitaData,
    addrProvincia,
    addrComune,
    addrCap,
    identDate,
  } = context;

  if (isPg) {
    const isComplete =
      !isMissing(pIva) &&
      pIva !== "00000000000" &&
      !isMissing(denominazione) &&
      !isMissing(addrProvincia) &&
      !isMissing(addrComune) &&
      !isMissing(identDate);

    const isNotCompleteReasons = isComplete
      ? ""
      : buildReasons([
          {
            when: isMissing(pIva) || pIva === "00000000000",
            message: "Partita Iva non presente\n",
          },
          { when: isMissing(denominazione), message: "Denominazione non presente\n" },
          { when: isMissing(addrProvincia), message: "Provincia non presente\n" },
          { when: isMissing(addrComune), message: "Comune non presente\n" },
          { when: isMissing(addrCap), message: "CAP non presente\n" },
          {
            when: isMissing(identDate),
            message: "Data di identificazione non presente",
          },
        ]);

    return { isComplete, isNotCompleteReasons };
  }

  const isComplete =
    !isMissing(cf) &&
    !isMissing(denominazione) &&
    !isMissing(professione) &&
    !isMissing(nascitaComune) &&
    !isMissing(nascitaData) &&
    !isMissing(addrProvincia) &&
    !isMissing(addrComune) &&
    !isMissing(identDate);

  const isNotCompleteReasons = isComplete
    ? ""
    : buildReasons([
        { when: isMissing(cf), message: "Codice Fiscale non presente\n" },
        { when: isMissing(denominazione), message: "Denominazione non presente\n" },
        { when: isMissing(professione), message: "Professione non presente\n" },
        {
          when: isMissing(nascitaComune),
          message: "Comune di nascita non presente\n",
        },
        { when: isMissing(nascitaData), message: "Data di nascita non presente\n" },
        { when: isMissing(addrProvincia), message: "Provincia non presente\n" },
        { when: isMissing(addrComune), message: "Comune non presente\n" },
        { when: isMissing(addrCap), message: "CAP non presente\n" },
        {
          when: isMissing(identDate),
          message: "Data di identificazione non presente",
        },
      ]);

  return { isComplete, isNotCompleteReasons };
};

export const evaluateMultiCompleteness = (context) => {
  const { isPg, cf, pIva, denominazione, addrProvincia, addrComune, addrCap } = context;

  if (isPg) {
    const isComplete =
      !isMissing(pIva) &&
      !isMissing(denominazione) &&
      !isMissing(addrProvincia) &&
      !isMissing(addrComune);

    const isNotCompleteReasons = isComplete
      ? ""
      : buildReasons([
          { when: isMissing(pIva), message: "Partita Iva non presente\n" },
          { when: isMissing(denominazione), message: "Denominazione non presente\n" },
          { when: isMissing(addrProvincia), message: "Provincia non presente\n" },
          { when: isMissing(addrComune), message: "Comune non presente\n" },
          { when: isMissing(addrCap), message: "CAP non presente\n" },
        ]);

    return { isComplete, isNotCompleteReasons };
  }

  const isComplete =
    !isMissing(cf) &&
    !isMissing(denominazione) &&
    !isMissing(addrProvincia) &&
    !isMissing(addrComune);

  const isNotCompleteReasons = isComplete
    ? ""
    : buildReasons([
        { when: isMissing(cf), message: "Codice Fiscale non presente\n" },
        { when: isMissing(denominazione), message: "Denominazione non presente\n" },
        { when: isMissing(addrProvincia), message: "Provincia non presente\n" },
        { when: isMissing(addrComune), message: "Comune non presente\n" },
        { when: isMissing(addrCap), message: "CAP non presente\n" },
      ]);

  return { isComplete, isNotCompleteReasons };
};

export const findExistingFascicolo = (fascicoliCustomerList, { isPg, cf, pIva }) => {
  const matched = fascicoliCustomerList.filter((x) =>
    isPg ? x.isPg === isPg && x.pIva === pIva : x.isPg === isPg && x.cf === cf
  );

  return matched.length > 0 ? matched[0] : null;
};

export const buildImportableRow = ({
  id,
  cf,
  pIva,
  denominazione,
  isPg,
  isComplete,
  isNotCompleteReasons,
}) => ({
  id,
  cf,
  pIva,
  denominazione,
  isPg,
  isComplete,
  isNotCompleteReason: isNotCompleteReasons,
});

export const pushUniqueFascicolo = (rows, fascicolo) => {
  if (rows.filter((x) => x.fascicoloId === fascicolo.fascicoloId).length <= 0) {
    rows.push(fascicolo);
  }
};

export const buildUploadRowsData = ({
  valuesArray,
  importType,
  isAlphaNumericFn,
  fascicoliCustomerList,
}) => {
  const importableRows = [];
  const existingFascicoliRows = [];
  const completenessEvaluator =
    importType === "multi" ? evaluateMultiCompleteness : evaluateAricCompleteness;

  for (let i = 0; i < valuesArray.length; i++) {
    try {
      const item = valuesArray[i];
      const context = getUploadRowContext({
        item,
        index: i,
        importType,
        isAlphaNumericFn,
      });

      const { id, cf, pIva, isPg, denominazione } = context;
      const include = shouldIncludeUploadRow(importableRows, {
        isPg,
        cf,
        pIva,
      });

      if (!include) {
        continue;
      }

      const { isComplete, isNotCompleteReasons } = completenessEvaluator(context);
      const fascicolo = findExistingFascicolo(fascicoliCustomerList, {
        isPg,
        cf,
        pIva,
      });

      if (fascicolo === null) {
        importableRows.push(
          buildImportableRow({
            id,
            cf,
            pIva,
            denominazione,
            isPg,
            isComplete,
            isNotCompleteReasons,
          })
        );
      } else {
        pushUniqueFascicolo(existingFascicoliRows, fascicolo);
      }
    } catch (e) {}
  }

  return {
    importableRows,
    existingFascicoliRows,
  };
};
